import { t, uiText, currentUiLanguage, resolveUiLanguage, languageLabel } from "../../../site/ui-language.js";
import React, { useEffect, useState } from "react";
import { getCachedSession, refreshSession } from "../client-cache.js";
import AppearanceSettingsButton from "../AppearanceSettings.jsx";

const MODULES = Object.freeze([
  { key: "lesen", label: "Lesen", href: "/lesen" },
  { key: "horen", label: "Hören", href: "/horen" },
  { key: "shreiben", label: "Schreiben", href: "/schreiben" },
  { key: "mundlich", label: "Sprechen", href: "/sprechen" },
  { key: "simulation", label: "Prüfung", href: "/simulation" }
]);

const DEFAULT_LEVELS = Object.freeze({
  home: "b1",
  lesen: "b1",
  horen: "b1",
  shreiben: "b2",
  mundlich: "b1"
});

const initialSession = getCachedSession();
let cachedSessionUser = initialSession ? initialSession.user || null : undefined;
let googleOneTapRequest = null;
let googleOneTapStarted = false;

function fetchSessionUser(force = false) {
  return refreshSession({ force })
    .then((payload) => {
      cachedSessionUser = payload?.user || null;
      return cachedSessionUser;
    });
}

const INSTALL_COPY = Object.freeze({
  ar: {
    button: "تثبيت التطبيق",
    title: "ثبّت ZDeutsch على جهازك",
    ios: "افتح قائمة المشاركة في Safari، ثم اختر «إضافة إلى الشاشة الرئيسية».",
    other: "افتح قائمة المتصفح واختر «تثبيت التطبيق» أو «إضافة إلى الشاشة الرئيسية».",
    close: "حسنًا"
  },
  fr: {
    button: "Installer l’app",
    title: "Installer ZDeutsch",
    ios: "Dans Safari, touchez Partager, puis « Sur l’écran d’accueil ».",
    other: "Ouvrez le menu du navigateur, puis choisissez « Installer l’application » ou « Ajouter à l’écran d’accueil ».",
    close: "Compris"
  },
  en: {
    button: "Install app",
    title: "Install ZDeutsch",
    ios: "In Safari, tap Share, then “Add to Home Screen”.",
    other: "Open your browser menu and choose “Install app” or “Add to Home Screen”.",
    close: "Got it"
  }
});

function installLanguage(nativeLanguage) { return resolveUiLanguage(nativeLanguage || currentUiLanguage()); }

function InstallIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3v11m0 0 4-4m-4 4-4-4M5 18v2h14v-2" />
    </svg>
  );
}

function PwaInstallButton({ nativeLanguage = "English", mobileMenu = false }) {
  const [pwaState, setPwaState] = useState(() => window.ZDeutschPwa?.getState?.() || { installed: false, canPrompt: false, platform: "other" });
  const [instructions, setInstructions] = useState(null);
  const language = installLanguage(nativeLanguage);
  const copy = INSTALL_COPY[language] || INSTALL_COPY.en;

  useEffect(() => window.ZDeutschPwa?.subscribe?.(setPwaState), []);

  if (pwaState.installed) return null;

  async function startInstall() {
    const result = await window.ZDeutschPwa?.install?.();
    if (!result || result.outcome === "instructions") setInstructions(result || { platform: "other" });
  }

  return (
    <>
      <button
        type="button"
        className={`zd-pwa-install${mobileMenu ? " is-menu" : ""}`}
        onClick={startInstall}
        aria-label={copy.button}
        title={copy.button}
      >
        <InstallIcon />
        <span>{copy.button}</span>
      </button>
      {instructions ? (
        <div className="zd-pwa-help" role="dialog" aria-modal="true" aria-labelledby="zd-pwa-help-title" dir={language === "ar" ? "rtl" : "ltr"}>
          <button className="zd-pwa-help__backdrop" type="button" aria-label={copy.close} onClick={() => setInstructions(null)} />
          <div className="zd-pwa-help__card">
            <span className="zd-pwa-help__icon"><InstallIcon /></span>
            <h2 id="zd-pwa-help-title">{copy.title}</h2>
            <p>{instructions.platform === "ios" ? copy.ios : copy.other}</p>
            <button type="button" onClick={() => setInstructions(null)}>{copy.close}</button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function getRequestedLevel(pageName, targetLevel = "") {
  if (targetLevel === "b1" || targetLevel === "b2") return targetLevel;
  const requested = new URLSearchParams(window.location.search).get("level")?.toLowerCase();
  return requested === "b1" || requested === "b2"
    ? requested
    : DEFAULT_LEVELS[pageName] || "b1";
}

function moduleHref(module, level) {
  const supportedLevel = module.levels?.includes(level) ? level : module.levels?.[0] || level;
  return `${module.href}?level=${encodeURIComponent(supportedLevel)}`;
}

function levelHref(pageName, level) {
  if (pageName === "dashboard") return `/dashboard?level=${level}`;
  if (pageName === "home") {
    return `/library?level=${level}`;
  }
  const module = MODULES.find((entry) => entry.key === pageName) || MODULES[0];
  return moduleHref(module, level);
}

function ResetIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4.9 7.3A8 8 0 1 1 4 12" />
      <path d="M4 4v4h4" />
    </svg>
  );
}

function SessionUser({ user }) {
  if (!user) return null;
  const displayName = user.displayName || user.email || "Learner";
  const initial = displayName.trim().charAt(0).toUpperCase() || "Z";
  return (
    <div className="zd-session-user">
      <a className="zd-session-profile-link" href={user.role === "zdeutsch-admin" ? "/admin" : "/dashboard"} aria-label={uiText(currentUiLanguage(), `Open dashboard for ${displayName}`)}>
        <span className="zd-session-avatar" aria-hidden="true">{initial}</span>
        <span className="zd-session-copy">
          <strong className="zd-session-name">{displayName}</strong>
          <span className="zd-session-meta">{languageLabel(user.nativeLanguage || "English")}</span>
        </span>
      </a>
      <form method="post" action="/logout">
        <button className="zd-session-logout" type="submit" aria-label={uiText(currentUiLanguage(), `Log out ${displayName}`)}>
          <span aria-hidden="true">↪</span><span className="zd-session-logout-label">{t("Log out")}</span>
        </button>
      </form>
    </div>
  );
}

function GuestActions({ mobile = false }) {
  return (
    <div className={mobile ? "zd-guest-actions is-mobile" : "zd-guest-actions"}>
      <a href={`/login?lang=${currentUiLanguage()}`}>{t("Anmelden")}</a>
      <a className="is-primary" href={`/register?lang=${currentUiLanguage()}`}>{t("Registrieren")}</a>
    </div>
  );
}

function loadGoogleOneTap() {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  if (googleOneTapRequest) return googleOneTapRequest;
  googleOneTapRequest = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-zdeutsch-google-one-tap]');
    const script = existing || document.createElement("script");
    script.addEventListener("load", () => {
      if (window.google?.accounts?.id) resolve(window.google.accounts.id);
      else reject(new Error("Google Identity Services did not load."));
    }, { once: true });
    script.addEventListener("error", () => reject(new Error("Google Identity Services could not load.")), { once: true });
    if (!existing) {
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.dataset.zdeutschGoogleOneTap = "true";
      document.head.append(script);
    }
  }).catch((error) => {
    googleOneTapRequest = null;
    throw error;
  });
  return googleOneTapRequest;
}

async function startGoogleOneTap() {
  if (googleOneTapStarted) return;
  googleOneTapStarted = true;
  try {
    const configResponse = await fetch("/google-onetap-config", { credentials: "same-origin", cache: "no-store" });
    const config = configResponse.ok ? await configResponse.json() : null;
    if (!config?.enabled || !config.clientId) return;
    const googleIdentity = await loadGoogleOneTap();
    googleIdentity.initialize({
      client_id: config.clientId,
      auto_select: false,
      cancel_on_tap_outside: true,
      use_fedcm_for_prompt: true,
      callback: async ({ credential } = {}) => {
        if (!credential) return;
        const response = await fetch("/app/auth/google/onetap", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ credential, mode: "login", source: "zdeutsch" })
        });
        const payload = await response.json().catch(() => ({}));
        if (response.ok && payload.redirectUrl) window.location.assign(payload.redirectUrl);
      }
    });
    googleIdentity.prompt();
  } catch {
    googleOneTapStarted = false;
  }
}

function navigationProps(href, onNavigate) {
  if (!onNavigate) return {};
  return {
    onClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      onNavigate(href);
    }
  };
}

function SimulationExamActions() {
  const [fullscreen, setFullscreen] = useState(() => !!window.parent.document.fullscreenElement);
  useEffect(()=>{const update=event=>setFullscreen(!!event.detail.active);window.addEventListener("zdeutsch-exam-fullscreen",update);return()=>window.removeEventListener("zdeutsch-exam-fullscreen",update);},[]);
  const [clock, setClock] = useState(() => window.ZDeutschExamWorkspace?.clock || { text: "--:--", urgent: false });
  useEffect(() => {
    const update = (event) => setClock(event.detail);
    window.addEventListener("zdeutsch-exam-clock", update);
    if (window.ZDeutschExamWorkspace?.clock) setClock(window.ZDeutschExamWorkspace.clock);
    return () => window.removeEventListener("zdeutsch-exam-clock", update);
  }, []);
  return <div className="zd-simulation-header-actions">
    <span id="sim-workspace-clock" className={`zd-simulation-header-clock${clock.urgent ? " is-urgent" : ""}`} role="timer" aria-label={t("Verbleibende Prüfungszeit")} dir="ltr">{clock.text}</span>
    <button className="zd-simulation-submit" type="button" data-exam-action="submit" aria-label={t("Prüfung abgeben")}><span className="zd-simulation-submit__full">{t("Prüfung abgeben")}</span><span className="zd-simulation-submit__short">{t("Abgeben")}</span></button>
    <button className="zd-simulation-fullscreen" type="button" data-exam-action="fullscreen" aria-label={fullscreen ? t("Vollbild verlassen") : t("Vollbild")} aria-pressed={fullscreen} title={fullscreen ? t("Vollbild verlassen") : t("Vollbild")}><svg viewBox="0 0 24 24" aria-hidden="true"><path d={fullscreen ? "M3 8h5V3m8 0v5h5M8 21v-5H3m18 0h-5v5" : "M8 3H3v5m13-5h5v5M3 16v5h5m8 0h5v-5"} /></svg></button>
    <button className="zd-simulation-quit" type="button" data-exam-action="quit" aria-label={t("Prüfung abbrechen")} title={t("Prüfung abbrechen")}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4H4v16h5M14 8l4 4-4 4M8 12h10" /></svg><span>{t("Abbrechen")}</span></button>
  </div>;
}

function SharedHeader({ pageName, children, catalog = false, onNavigate }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sessionUser, setSessionUser] = useState(() => cachedSessionUser || null);
  const [sessionResolved, setSessionResolved] = useState(() => cachedSessionUser !== undefined);
  const navigationPageName = pageName === "correction" ? "shreiben" : pageName;
  const isAdmin = sessionUser?.role === "zdeutsch-admin";
  const targetLevel = !isAdmin && ["b1", "b2"].includes(sessionUser?.targetLevel) ? sessionUser.targetLevel : "";
  const currentLevel = getRequestedLevel(navigationPageName, targetLevel);
  const isSpeaking = navigationPageName === "mundlich";
  const isLibrary = pageName === "home";
  const homeHref = "/";
  const runningSimulation = Boolean(window.ZDeutschExamWorkspace && !window.ZDeutschExamWorkspace.boot.finished);
  const inExam = Boolean(window.ZDeutschExamWorkspace) || (!catalog && ["lesen", "horen", "shreiben", "mundlich"].includes(pageName));
  const navigationModules = MODULES.filter((module) => module.key !== "simulation" || !inExam);
  const accountContext = {
    subscription: ["Mitgliedschaft", "Wählen oder verwalten Sie Ihren ZDeutsch-Plan."],
    payment: ["Zahlung", "Wählen Sie PayPal oder Banküberweisung und Ihre bevorzugte Währung."],
    profile: ["Profil", "Plan, Nutzung und Passwort verwalten."],
    dashboard: ["Dashboard", "Bibliothek, Profil und Lernfortschritt an einem Ort."],
    matching_game: ["Matching game", "Verbinden Sie Wörter mit den passenden Bedeutungen."],
    onboarding: ["Profil vervollständigen", "Ergänzen Sie Ihre Kontaktdaten, bevor Sie einen Plan wählen."],
    admin: ["Verwaltung", "Benutzer und ZDeutsch-Abonnements verwalten."],
    admin_requests: ["Änderungsanfragen", "Plan- und Niveauänderungen prüfen."]
  }[pageName];

  useEffect(() => {
    let active = true;
    const loadUser = (event) => {
      fetchSessionUser(Boolean(event))
        .then((nextUser) => {
          if (active) {
            setSessionUser(nextUser);
            setSessionResolved(true);
          }
        })
        .catch(() => {
          if (active) setSessionResolved(true);
        });
    };
    loadUser(null);
    window.addEventListener("zdeutsch-session-updated", loadUser);
    return () => {
      active = false;
      window.removeEventListener("zdeutsch-session-updated", loadUser);
    };
  }, []);

  useEffect(() => {
    if (sessionResolved && !sessionUser) startGoogleOneTap();
  }, [sessionResolved, sessionUser]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };
    document.body.classList.add("zd-mobile-menu-open");
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("zd-mobile-menu-open");
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileMenuOpen]);

  const mobileNavigationProps = (href) => navigationProps(href, (target) => {
    setMobileMenuOpen(false);
    if (onNavigate) onNavigate(target);
    else window.location.assign(target);
  });

  if (pageName === "onboarding") {
    const language = installLanguage(sessionUser?.nativeLanguage);
    const logoutLabel = language === "ar" ? "تسجيل الخروج" : language === "fr" ? "Se déconnecter" : t("Log out");
    return <div className="zd-global-header__main zd-profile-required-header">
      <span className="zd-global-brand"><img src="logo.svg?v=20260312-1" alt="ZDeutsch" /></span>
      <div className="zd-session-user">
        {sessionUser ? <span className="zd-session-copy"><strong className="zd-session-name">{sessionUser.displayName || sessionUser.email || "Learner"}</strong></span> : null}
        <form method="post" action="/logout"><button className="zd-session-logout" type="submit"><span aria-hidden="true">↪</span><span className="zd-session-logout-label">{logoutLabel}</span></button></form>
      </div>
    </div>;
  }

  return (
    <>
      <div className="zd-global-header__main">
        <div className="zd-global-brand-group">
          <button
            className="zd-mobile-menu-toggle"
            type="button"
            aria-label={t("Navigation öffnen")}
            aria-controls="zd-mobile-navigation"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(true)}
          >
            <span /><span /><span />
          </button>
          <a className="zd-global-brand" href={homeHref} aria-label={t("ZDeutsch start")} {...navigationProps(homeHref, onNavigate)}>
            <img src="logo.svg?v=20260312-1" alt="ZDeutsch" />
          </a>
        </div>

        <nav className="zd-global-modules" aria-label={t("Prüfungsbereiche")}>
          {navigationModules.map((module) => (
            <a
              key={module.key}
              className={module.key === navigationPageName || module.key === "simulation" && runningSimulation ? "is-active" : ""}
              href={moduleHref(module, currentLevel)}
              {...navigationProps(moduleHref(module, currentLevel), onNavigate)}
              aria-current={module.key === navigationPageName ? "page" : undefined}
            >
              {module.label}
            </a>
          ))}
        </nav>

        <div className="zd-global-header__end">
          {pageName === "mundlich" || catalog || isLibrary ? <AppearanceSettingsButton language={installLanguage(sessionUser?.nativeLanguage)} /> : null}
          <PwaInstallButton nativeLanguage={sessionUser?.nativeLanguage} />
          {runningSimulation ? <SimulationExamActions /> : <div className={`zd-global-levels${targetLevel ? " is-fixed" : ""}`} aria-label={targetLevel ? t("Target level") : t("Niveau wählen")}>
            <span className="zd-global-levels__label">{t("Niveau")}</span>
            {(targetLevel ? [targetLevel] : ["b1", "b2"]).map((level) => {
              return (
                <a
                  key={level}
                  className={currentLevel === level ? "is-active" : ""}
                  href={levelHref(navigationPageName, level)}
                  {...navigationProps(levelHref(navigationPageName, level), onNavigate)}
                  aria-current={currentLevel === level ? "true" : undefined}
                  title={`${level.toUpperCase()} öffnen`}
                >
                  {level.toUpperCase()}
                </a>
              );
            })}
          </div>}
          <div id="zd-global-user-slot" className="zd-global-user-slot">
            {sessionUser ? <SessionUser user={sessionUser} /> : sessionResolved ? <GuestActions /> : null}
          </div>
        </div>
      </div>

      <button
        className={`zd-mobile-menu-backdrop${mobileMenuOpen ? " is-open" : ""}`}
        type="button"
        aria-label={t("Navigation schließen")}
        tabIndex={mobileMenuOpen ? 0 : -1}
        onClick={() => setMobileMenuOpen(false)}
      />
      <aside
        id="zd-mobile-navigation"
        className={`zd-mobile-menu${mobileMenuOpen ? " is-open" : ""}`}
        aria-hidden={!mobileMenuOpen}
      >
        <div className="zd-mobile-menu__top">
          <a href={homeHref} {...mobileNavigationProps(homeHref)}>
            <img src="logo.svg?v=20260312-1" alt="ZDeutsch" />
          </a>
          <button type="button" aria-label={t("Navigation schließen")} onClick={() => setMobileMenuOpen(false)}>×</button>
        </div>
        <nav className="zd-mobile-menu__modules" aria-label={t("Prüfungsbereiche")}>
            {navigationModules.map((module) => {
              const href = moduleHref(module, currentLevel);
              return (
              <a
                key={module.key}
                className={module.key === navigationPageName || module.key === "simulation" && runningSimulation ? "is-active" : ""}
                href={href}
                {...mobileNavigationProps(href)}
                aria-current={module.key === navigationPageName ? "page" : undefined}
              >
                <span>{module.label}</span><small>{currentLevel.toUpperCase()}</small>
              </a>
            );
          })}
        </nav>
        {!runningSimulation ? <div className="zd-mobile-menu__levels">
          <span>{t("Niveau")}</span>
          <div>
            {(targetLevel ? [targetLevel] : ["b1", "b2"]).map((level) => {
              const href = levelHref(navigationPageName, level);
              return (
                <a
                  key={level}
                  className={currentLevel === level ? "is-active" : ""}
                  href={href}
                  {...mobileNavigationProps(href)}
                >{level.toUpperCase()}</a>
              );
            })}
          </div>
        </div> : null}
        <div className="zd-mobile-menu__account">
          <div className="zd-mobile-appearance"><span>{installLanguage(sessionUser?.nativeLanguage) === "ar" ? "الإعدادات" : t("Settings")}</span><AppearanceSettingsButton language={installLanguage(sessionUser?.nativeLanguage)} /></div>
          <PwaInstallButton nativeLanguage={sessionUser?.nativeLanguage} mobileMenu />
          {sessionUser ? (
            <>
              <a href="/dashboard" {...mobileNavigationProps("/dashboard")}>{t("Dashboard")}</a>
              <a href="/matching-game" {...mobileNavigationProps("/matching-game")}>{t("Matching game")}</a>
              <a href="/subscription" {...mobileNavigationProps("/subscription")}>{t("Mitgliedschaft")}</a>
            </>
          ) : <GuestActions mobile />}
          {!sessionUser ? <a href="/library" {...mobileNavigationProps("/library")}>{t("Bibliothek")}</a> : null}
        </div>
      </aside>

      {pageName === "correction" ? (
        <div className="zd-exam-context zd-library-context">
          <span>{t("Korrekturvorschläge")}</span>
          <p>{t("Verbesserungen für die Übungsbibliothek einreichen.")}</p>
        </div>
      ) : catalog || isLibrary ? (
        <div className="zd-exam-context zd-library-context">
          <span>{t(accountContext?.[0]) || (isLibrary ? t("Prüfungsbibliothek") : `${MODULES.find((module) => module.key === pageName)?.label || "Übungen"} · ${currentLevel.toUpperCase()}`)}</span>
          <p>{t(accountContext?.[1]) || (isLibrary ? t("Wählen Sie oben einen Bereich oder ein Niveau.") : t("Wählen Sie eine Übung aus der Liste."))}</p>
        </div>
      ) : isSpeaking ? (
        <div className="zd-exam-context zd-speaking-context">
          <div className="zd-speaking-context__title">
            <span>{t("Sprechtraining")}</span>
            <strong>{currentLevel.toUpperCase()}</strong>
          </div>
          <div className="oral-header-actions">
            <button id="timer-toggle" className="oral-timer-button" type="button" aria-label={t("Timer starten")}>
              <span className="oral-timer-dot" aria-hidden="true" />
              <span id="timer-value">03:00</span>
            </button>
            <button id="timer-reset" className="oral-icon-button" type="button" aria-label={t("Timer zurücksetzen")} title={t("Timer zurücksetzen")}>
              <ResetIcon />
            </button>
          </div>
        </div>
      ) : (
        <div className="zd-exam-context">{children}</div>
      )}
    </>
  );
}

export function ExamHeader({ pageName, className = "", children, catalog = false, onNavigate, ...props }) {
  return (
    <header
      {...props}
      className={`${className} zd-exam-header`.trim()}
      data-exam-module={pageName}
    >
      <SharedHeader pageName={pageName} catalog={catalog} onNavigate={onNavigate}>{children}</SharedHeader>
    </header>
  );
}

export function ExamFooter({ pageName, className = "", children, ...props }) {
  const runningSimulation = pageName === "shreiben" && window.ZDeutschExamWorkspace && !window.ZDeutschExamWorkspace.boot.finished;
  const replaceLibraryLink = (nodes) => React.Children.map(nodes, (node) => {
    if (!React.isValidElement(node)) return node;
    if (node.props.id === "shreiben-home-link") return [
      <button key="previous" className="zd-simulation-previous" type="button" data-exam-action="previous">{t("Zurück")}</button>,
      <button key="submit" id="sim-final-submit" className="zd-simulation-submit" type="button" data-exam-action="submit">{t("Prüfung abgeben")}</button>
    ];
    return node.props.children ? React.cloneElement(node, {}, replaceLibraryLink(node.props.children)) : node;
  });
  const adaptedChildren = runningSimulation ? replaceLibraryLink(children) : children;
  const footerChildren = ["lesen", "horen"].includes(pageName) || runningSimulation
    ? React.Children.map(adaptedChildren, (node) => {
      if (!React.isValidElement(node) || node.type !== "div") return node;
      const items = React.Children.toArray(node.props.children).filter(React.isValidElement);
      const actions = runningSimulation ? items.slice(1) : React.Children.toArray(items[1]?.props.children);
      const previous = actions.find((action) => React.isValidElement(action) &&
        (["prev-btn", "horen-prev-btn"].includes(action.props.id) || action.props["data-exam-action"] === "previous"));
      if (!previous) return node;
      return React.cloneElement(node, { className: `${node.props.className || ""} zd-footer-layout`.trim() },
        <div className="zd-footer-back">{previous}</div>,
        <div className="zd-footer-brand">{items[0]}</div>,
        <div className="zd-footer-forward">{actions.filter((action) => action !== previous)}</div>
      );
    }) : adaptedChildren;
  return (
    <footer
      {...props}
      className={`${className} zd-exam-footer`.trim()}
      data-exam-module={pageName}
    >
      {footerChildren}
    </footer>
  );
}
